import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getAgeRangeText } from '@/lib/utils';
import {
  GET_ONE_SESSION,
  GET_OVERALL_ASSESSMENT,
  NOTES_FROM_PREVIOUS_SESSIONS,
} from '@/pages/sessionDetails/GraphQl/queries';
// import { GET_ALL_SESSIONS } from '@/pages/sessions/GraphQl/queries';
import { GetUserByIdQuery, WeightType } from '@/Types/gql/graphql';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import React, { useMemo } from 'react';

type CustomerDetailProps = {
  customerData: GetUserByIdQuery | null;
  loading: boolean;
  customerId: string;
  sessionStartTime: string | null;
};

const CustomerDetail: React.FC<CustomerDetailProps> = ({ customerData, loading, customerId, sessionStartTime }) => {
  const { data: overallAssessmentData } = useQuery(GET_OVERALL_ASSESSMENT, {
    variables: {
      traineeId: customerId || '',
    },
    fetchPolicy: 'network-only',
    skip: !customerId,
  });


  // const { data } = useQuery(GET_ALL_SESSIONS, {
  //   variables: {
  //     search: '',
  //     limit: 5,
  //     offset: 0,
  //     sort: {
  //       sessionEndTime: -1,
  //     },
  //     filter: {
  //       traineeId: customerId || '',
  //       sessionStatus: 'Completed',
  //     },
  //   },
  //   fetchPolicy: 'network-only',
  //   skip: !customerId,
  // });

  const lastSessionFilter = useMemo(() => {
    if (
      !customerId ||
      !sessionStartTime
    ) {
      return null; // Return null if data is incomplete
    }

    return {
      traineeId: customerId,
      sessionStatus: 'Completed',
      sessionStartTime: {
        $lt: dayjs(sessionStartTime).toString(),
      },
    };
  }, [customerId, sessionStartTime]);
  const { data: lastSession } = useQuery(GET_ONE_SESSION, {
    skip: !lastSessionFilter,
    fetchPolicy: 'network-only',
    variables: {
      filter: lastSessionFilter,
      sort: {
        _id: -1,
      },
    },
  });
  // console.log("lastSessions", lastSessions);
  // const lastSession = data?.getAllSession;
  const { data: noteData } = useQuery(NOTES_FROM_PREVIOUS_SESSIONS, {
    variables: {
      limit: 5,
      offset: 0,
      sort: {
        sessionEndTime: -1,
      },
      filter: {
        traineeId: customerId || '',
        clientFeedback: { $ne: null },
        sessionStatus: 'Completed',
        _id: { $ne: lastSession && lastSession.getOneSession?._id },
      },
      formats: {
        edits: {
          resize: {
            width: 200,
            height: 200,
            fit: 'cover',
          },
        },
      },
    },
    fetchPolicy: 'network-only',
    skip: !customerId,
  });

  const notes = noteData?.getNotesFromPreviosSessions;



  if (loading) {
    return (
      <div
        className='flex flex-col text-white p-[18px] rounded-[20px] w-full mt-5 animate-pulse'
        style={{
          background:
            'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
        }}
      >
        <Skeleton className='w-full h-14 rounded-full' />
      </div>
    );
  }

  return (
    <div
      className='flex flex-col text-white p-[18px] rounded-[20px] w-full mt-5'
      style={{
        background:
          'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
      }}
    >
      <div className='flex w-full '>
        <div className='flex gap-2'>
          <Avatar className='w-14 h-14 bg-[#515764]'>
            {loading ? (
              <Skeleton className='w-14 h-14' />
            ) : (
              <>
                <AvatarImage
                  src={customerData?.getUserById?.profileImage || ''}
                  alt='Profile picture'
                  className='object-cover'
                />
                <AvatarFallback className='text-3xl font-bold'>
                  {customerData?.getUserById?.firstName?.charAt(0) ||
                    '' + customerData?.getUserById?.lastName?.charAt(0) ||
                    ''}
                </AvatarFallback>
              </>
            )}
          </Avatar>
          <div className='flex flex-col items-start ml-2'>
            <span className='text-2xl font-semibold'>
              {customerData?.getUserById?.firstName + ' ' + (customerData?.getUserById?.lastName ? customerData?.getUserById?.lastName : '')}
            </span>
            <p className='text-sm text-gray-400'>
              {customerData?.getUserById?.ageRange
                ? `${getAgeRangeText(customerData.getUserById.ageRange)}, `
                : ''}
              <span className='capitalize'>{customerData?.getUserById?.gender?.toLowerCase()}</span>
            </p>
          </div>
        </div>
      </div>
      <Tabs defaultValue='overview' className='w-full mt-5'>
        <TabsList className='flex gap-5 p-1 rounded-lg justify-start w-fit'>
          <TabsTrigger
            value='overview'
            className='
              data-[state=active]:bg-white data-[state=active]:text-black
              data-[state=inactive]:bg-[#343840] data-[state=inactive]:text-white
              px-4 py-2 rounded-lg transition-colors
            '
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value='last-training'
            className='
                data-[state=active]:bg-white data-[state=active]:text-black
                data-[state=inactive]:bg-[#343840] data-[state=inactive]:text-white
                px-4 py-2 rounded-lg transition-colors
              '
          >
            Last Training Session
          </TabsTrigger>
        </TabsList>
        <TabsContent value='overview'>
          <div className='w-full p-4 flex flex-col gap-4'>
            {/* Weight & Height */}
            <div className='grid grid-cols-2 gap-4 border-b border-gray-700 pb-2'>
              <div>
                <div className='text-sm font-semibold'>Weight</div>
                <div className='font-normal text-sm text-[#E2E4E8]'>
                  {customerData?.getUserById?.weight == null ||
                    customerData?.getUserById?.weight <= 0
                    ? 'N/A'
                    : customerData?.getUserById?.weightType === WeightType.Kilogram
                      ? `${(customerData.getUserById.weight / 1000).toFixed(2)} kg`
                      : `${(customerData.getUserById.weight * 0.00220462).toFixed(2)} lbs`}
                </div>
              </div>
              <div>
                <div className='text-sm font-semibold'>Height</div>
                <div className='font-normal text-sm text-[#E2E4E8]'>
                  {customerData?.getUserById?.height == null ||
                    customerData?.getUserById?.height <= 0
                    ? 'N/A'
                    : `${(customerData.getUserById?.height * 0.0328084).toFixed(2)} ft`}
                </div>
              </div>
            </div>
            {/* Overall Performance */}
            <div>
              <div className='text-base text-white mb-2 items-start font-semibold text-left'>
                Overall Performance
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Mobility</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer2.toFixed(1) ?? 0}
                  </span>
                </div>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Fitness Level</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer3.toFixed(1) ?? 0}
                  </span>
                </div>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Flexibility</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer4.toFixed(1) ?? 0}
                  </span>
                </div>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Strength</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer5.toFixed(1) ?? 0}
                  </span>
                </div>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Strength Endurance</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer6.toFixed(1) ?? 0}
                  </span>
                </div>
                <div className='bg-[#232323] rounded p-4 flex justify-between'>
                  <span className='text-white font-medium'>Familiarity with Fitness Tools</span>
                  <span className='text-[#E9CC7C] font-semibold'>
                    {overallAssessmentData?.getOverAllAssessment?.answer7.toFixed(1) ?? 0}
                  </span>
                </div>
              </div>
            </div>
            {/* Questions Section */}
            <div className='mt-6 flex flex-col gap-4'>
              {/* <div>
                <div className='text-sm font-semibold text-white text-left'>
                  Primary Fitness Goals
                </div>
                <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                  {customerData?.getUserById?.fitnessGoals || 'not-mentioned'}
                </div>
              </div> */}
              {/* <div>
                <div className='text-sm font-semibold text-white text-left'>
                  Preferred Types of Workouts
                </div>
                <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                  {customerData?.getUserById?.preferredWorkouts
                    ?.map((w) => w?.title)
                    .filter(Boolean)
                    .join(', ') || 'not-mentioned'}
                </div>
              </div> */}
              <div>
                <div className='text-sm font-semibold text-white text-left'>
                  Do you have any equipment at home?
                </div>
                <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                  {customerData?.getUserById?.equipments?.filter(Boolean).join(', ') ||
                    'not-mentioned'}
                </div>
              </div>
              <div>
                <div className='text-sm font-semibold text-white text-left'>
                  Any additional information you would like to share regarding your fitness goals or
                  current fitness level?
                </div>
                <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                  {customerData?.getUserById?.injury?.status
                    ? `Yes${customerData?.getUserById?.injury?.description ? `, ${customerData.getUserById.injury.description}` : ''}`
                    : 'No'}
                </div>
              </div>
              {/* <div>
                <div className='text-sm font-semibold text-white text-left'>
                  How often do you prefer to work out?
                </div>
                <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                  {customerData?.getUserById?.workoutPreference || 'not-mentioned'}
                </div>
              </div> */}
            </div>
          </div>
        </TabsContent>
        {lastSession &&
          lastSession.getOneSession ? (
          <TabsContent value='last-training'>
            <div className='w-full p-4 flex flex-col gap-4'>
              {/* Weight & Height */}
              <div className='grid grid-cols-2 gap-4 border-b border-gray-700 pb-2 text-left'>
                <span className='text-white font-semibold'>
                  {lastSession?.getOneSession?.programName && lastSession?.getOneSession?.sessionEndTime ? (
                    <span className='flex items-center gap-2 whitespace-nowrap'>
                      {lastSession?.getOneSession?.programName === 'FitPrime' ? lastSession?.getOneSession?.programName + `(${lastSession?.getOneSession?.trainingType?.title})` : lastSession?.getOneSession?.programName} on{' '}
                      {dayjs(lastSession?.getOneSession?.sessionEndTime).format('MM/DD/YYYY')} with
                      {lastSession?.getOneSession?.lastTrainerDtls?.profileImage && (
                        <img
                          src={lastSession?.getOneSession?.lastTrainerDtls.profileImage}
                          alt={lastSession?.getOneSession?.lastTrainerDtls.displayName || 'Trainer'}
                          className='w-6 h-6 rounded-full ml-2'
                        />
                      )}
                      <span className='ml-1'>
                        {lastSession?.getOneSession?.lastTrainerDtls?.displayName ?? ''}
                      </span>
                    </span>
                  ) : (
                    'N/A '
                  )}
                </span>
              </div>

              <div>
                <div className='text-base text-white mb-2 items-start font-semibold text-left'>
                  Performance
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Mobility</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer2.toFixed(1) : 0}
                    </span>
                  </div>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Fitness Level</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer3.toFixed(1) : 0}
                    </span>
                  </div>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Flexibility</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer4.toFixed(1) : 0}
                    </span>
                  </div>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Strength</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer5.toFixed(1) : 0}
                    </span>
                  </div>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Strength Endurance</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer6.toFixed(1) : 0}
                    </span>
                  </div>
                  <div className='bg-[#232323] rounded p-4 flex justify-between'>
                    <span className='text-white font-medium'>Familiarity with Fitness Tools</span>
                    <span className='text-[#E9CC7C] font-semibold'>
                      {lastSession?.getOneSession?.assesment ? lastSession?.getOneSession?.assesment?.assessment?.answer7.toFixed(1) : 0}
                    </span>
                  </div>
                </div>
              </div>
              {/* Questions Section */}
              <div className='mt-6 flex flex-col gap-4'>
                <div>
                  <div className='text-sm font-semibold text-white text-left'>
                    Client Performance
                  </div>
                  <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                    {lastSession?.getOneSession?.clientPerformance
                      ? capitalize(lastSession?.getOneSession?.clientPerformance.replace(/_/g, ' '))
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className='text-sm font-semibold text-white text-left'>
                    Targeted Muscle Group
                  </div>
                  <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                    {Array.isArray(lastSession?.getOneSession?.targetedMuscle) &&
                      lastSession?.getOneSession?.targetedMuscle.length > 0
                      ? lastSession?.getOneSession?.targetedMuscle.join(', ')
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className='text-sm font-semibold text-white text-left'>
                    Client Feedback/ Notes for Future Sessions{' '}
                  </div>
                  <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                    {lastSession?.getOneSession?.clientFeedback ?? 'N/A'}
                  </div>
                </div>
                <div>
                  <div className='text-sm font-semibold text-white text-left'>
                    Exercises Performed{' '}
                  </div>
                  <div className='text-base text-gray-300 border-b border-gray-700 pb-2 text-left'>
                    {lastSession?.getOneSession?.exercisePerformed || 'N/A'}
                  </div>
                </div>
              </div>
              {/* Notes from previous Training Sessions */}
              <div className='bg-[#00001C] rounded-xl p-4 mt-4'>
                <div className='text-white font-semibold mb-3 text-left'>
                  Notes from previous Training Sessions
                </div>
                {
                  notes && notes.length > 0
                    ? notes.map((note, idx) =>
                      note ? (
                        <React.Fragment key={note._id}>
                          <div className='mb-4'>
                            <div className='text-gray-100 text-left mb-2'>
                              {note.clientFeedback}
                            </div>
                            <div className='flex items-center gap-2'>
                              <img
                                src={note.trainerIdUser?.profileImage || '/default-avatar.png'}
                                alt={note.trainerIdUser?.displayName || 'Trainer'}
                                className='w-7 h-7 rounded-full'
                              />
                              <span className='font-semibold text-white text-sm'>
                                {note.trainerIdUser?.displayName || 'Unknown'}
                              </span>
                              <span className='text-xs text-gray-400 ml-2'>
                                {note.sessionEndTime
                                  ? dayjs(note.sessionEndTime).format('MM/DD/YY')
                                  : ''}
                              </span>
                            </div>
                          </div>
                          {idx !== notes.length - 1 && <hr className='border-gray-700 my-2' />}
                        </React.Fragment>
                      ) : null,
                    )
                    : ''
                  // <div className='text-gray-400'>No previous notes found.</div>
                }
              </div>
            </div>
          </TabsContent>
        ) : (
          <div className='w-full p-4 flex flex-col gap-4'>
            <div className='text-white font-semibold text-left'>
              This customer hasn&rsquo;t completed any training sessions yet.
            </div>
          </div>
        )}
      </Tabs>
    </div>
  );
};

export default CustomerDetail;
